import { useOktaAuth } from "@okta/okta-react";
import axios from "axios";
import { GetCustomError } from "shared/helpers/AxiosHelpers";

import { useQuery } from "@tanstack/react-query";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";

// Error message constants
const ERROR_MESSAGES = {
  SUBQUERY_ERROR: "Subquery returned more than 1 value",
  USER_FRIENDLY_MESSAGE:
    "There is a setup issue for your locations which return multiple aging periods. Please contact Net-Rx.",
  NETWORK_ERROR: "Network error",
};

// Error titles
const ERROR_TITLES = {
  AGING_PERIODS_ERROR: "Error Loading Aging Periods",
  GENERIC_ERROR: "Error",
};

export function useFetchAgingPeriods(apiParameters) {
  const { oktaAuth } = useOktaAuth();

  const dataKey = "useFetchAgingPeriodsDataKey";

  // Helper function to check if error contains subquery issue
  function isSubqueryError(errorMessage) {
    if (!errorMessage) return false;

    const messageStr = errorMessage.toString().toLowerCase();
    // Only return true if we explicitly find the subquery error message
    // Check for the exact SQL Server error message (case-insensitive)
    return messageStr.includes("subquery returned more than 1 value");
  }

  // Helper function to create user-friendly error
  function createUserFriendlyError(description, additionalProps = {}) {
    return {
      message: ERROR_MESSAGES.USER_FRIENDLY_MESSAGE,
      userMessage: ERROR_MESSAGES.USER_FRIENDLY_MESSAGE,
      errorTitle: ERROR_TITLES.AGING_PERIODS_ERROR,
      description,
      ...additionalProps,
    };
  }

  async function GetAgingPeriods() {
    let accessToken = oktaAuth.getAccessToken();

    var result = await axios
      .get("api/Aging/GetAgingPeriods", {
        params: apiParameters,
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      .then((response) => {
        return response.data;
      })
      .catch((err) => {
        // Use GetCustomError for errors with response
        if (err && err.response) {
          let thisError = GetCustomError(err);

          // Check if the error contains the SQL subquery error
          // Check both the description and the original response data
          const errorMessage =
            thisError.description || (err.response && err.response.data) || "";

          if (isSubqueryError(errorMessage)) {
            throw createUserFriendlyError(thisError.description, {
              ...thisError,
            });
          }

          // Add generic error title for other errors
          thisError.errorTitle = ERROR_TITLES.GENERIC_ERROR;
          throw thisError;
        } else {
          // Handle network or other errors without response
          const error = new Error(
            (err && err.message) || ERROR_MESSAGES.NETWORK_ERROR
          );
          error.code = -1;
          error.description =
            (err && err.message) || ERROR_MESSAGES.NETWORK_ERROR;
          error.errorTitle = ERROR_TITLES.GENERIC_ERROR;
          throw error;
        }
      });

    return result;
  }

  function IsEnabled() {
    if (IsNullOrEmpty(apiParameters)) return false;
    return true;
  }

  return useQuery({
    queryKey: [dataKey, apiParameters],
    queryFn: async () => await GetAgingPeriods(),
    enabled: IsEnabled,
    refetchOnWindowFocus: false,
  });
}
